// Multi-family: Residents CRUD page (P1-10).
//
// Invite path:
//   - Operator may tick "Send invite email" on create — backend emails
//     a portal setup link and returns `{ resident, invite_token }`.
//   - The raw `invite_token` is sensitive. The UI hides it by default.
//     Only master_admin with the "show link" master-only checkbox ticked
//     ever sees the full link, and surfacing it audits as part of the
//     backend RESIDENT_INVITED entry (the link is already a one-shot
//     token tied to a reset row that the audit log captures).

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button, Icon } from '@ng-unifi/ui';
import {
  CreateResidentBody,
  ResidentRelationship,
  UpdateResidentBody,
} from '@ng-unifi/domain';
import { apiClient, ApiError } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { useAuth } from '../auth/AuthContext.js';
import { CrudTable, fieldCls } from '../components/CrudTable.js';
import { PropertyScopeBar, scopedHref, usePropertyScope } from '../components/PropertyScope.js';
import { SearchInput } from '../components/SearchInput.js';
import { CrudModal, FormField, isLikelyEmail } from '../components/CrudModal.js';
import { RowActions, Toast } from '../components/RowActions.js'; // Toast used by InviteToast below
import { LocalOnlyBanner } from '../components/LocalOnlyBanner.js';

interface ResidentRow {
  id: string;
  household_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  relationship: 'leaseholder' | 'occupant' | 'minor' | 'caregiver';
  user_id: string | null;
}

interface HouseholdLite {
  id: string;
  unit_id: string;
  label: string | null;
  moved_out_at: string | null;
}

interface UnitLite {
  id: string;
  building_id: string;
  unit_number: string;
}

interface BuildingLite {
  id: string;
  name: string;
}

async function loadResidents(): Promise<ResidentRow[]> {
  const res = await apiClient.get<{ data: ResidentRow[] }>('/api/v1/residents');
  return res.data;
}

async function loadHouseholds(): Promise<HouseholdLite[]> {
  const res = await apiClient.get<{ data: HouseholdLite[] }>('/api/v1/households');
  return res.data;
}

async function loadUnits(): Promise<UnitLite[]> {
  const res = await apiClient.get<{ data: UnitLite[] }>('/api/v1/units');
  return res.data;
}

async function loadBuildings(): Promise<BuildingLite[]> {
  const res = await apiClient.get<{ data: BuildingLite[] }>('/api/v1/buildings');
  return res.data;
}

const REL_TONE: Record<ResidentRow['relationship'], string> = {
  leaseholder: 'bg-primary-container text-on-primary',
  occupant: 'bg-secondary-container text-on-secondary-container',
  minor: 'bg-tertiary-fixed text-on-tertiary-fixed',
  caregiver: 'bg-surface-container-high text-on-surface-variant',
};

type ModalKind = 'create' | 'edit' | 'remove';
type ModalState = { kind: ModalKind; row: ResidentRow | null };

export function ResidentsPage() {
  const [rows, setRows] = useState<ResidentRow[] | null>(null);
  const [households, setHouseholds] = useState<HouseholdLite[]>([]);
  const [units, setUnits] = useState<UnitLite[]>([]);
  const [buildings, setBuildings] = useState<BuildingLite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<{ msg: string; inviteToken?: string } | null>(
    null,
  );

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    Promise.all([loadResidents(), loadHouseholds(), loadUnits(), loadBuildings()])
      .then(([rs, hs, us, bs]) => {
        if (cancelled) return;
        setRows(rs);
        setHouseholds(hs);
        setUnits(us);
        setBuildings(bs);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  // Scoped from Households — the last level of the drill-down.
  const { buildingId, unitId, householdId } = usePropertyScope();

  const householdLabel = (id: string) => {
    const h = households.find((x) => x.id === id);
    if (!h) return id;
    const u = units.find((x) => x.id === h.unit_id);
    const b = u ? buildings.find((x) => x.id === u.building_id) : undefined;
    const left = h.label ?? 'Household';
    if (b && u) return `${left} · ${b.name} · ${u.unit_number}`;
    if (u) return `${left} · ${u.unit_number}`;
    return left;
  };

  const householdsActive = useMemo(
    () => households.filter((h) => !h.moved_out_at),
    [households],
  );

  const [search, setSearch] = useState('');
  const filteredRows = useMemo(() => {
    if (!rows) return null;
    const q = search.trim().toLowerCase();
    if (q.length === 0) return rows;
    return rows.filter((r) => {
      if (r.name.toLowerCase().includes(q)) return true;
      if (r.email && r.email.toLowerCase().includes(q)) return true;
      // householdLabel reads households + units + buildings, all in deps.
      if (householdLabel(r.household_id).toLowerCase().includes(q)) return true;
      return false;
    });
  }, [rows, search, households, units, buildings]);

  const scopedRows = householdId
    ? (filteredRows ?? []).filter((r) => r.household_id === householdId)
    : filteredRows;

  return (
    <div className="space-y-6">
      {/* flex-wrap + min-w-0: at 320px the icon, title and the action button
          cannot share one row, and the button is what has to move. Same
          header shape as HouseholdsPage. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="person" className="text-2xl" />
          </div>
          <div className="min-w-0">
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Residents
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              People living at this property.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setModal({ kind: 'create', row: null })}
          disabled={householdsActive.length === 0}
        >
          <Icon name="add" />
          <span>Add resident</span>
        </Button>
      </header>

      <LocalOnlyBanner
        title="Local records only"
        body="Resident records and portal invites are local. Credentials and door-policy push to the UniFi controller land in a follow-up tranche."
      />

      {toast ? (
        <InviteToast
          toast={toast}
          onDismiss={() => setToast(null)}
        />
      ) : null}

      {rows !== null && (rows.length > 0 || search.length > 0) ? (
        <div className="flex items-center gap-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search residents by name, email, or unit"
            id="residents-search"
          />
          {search.length > 0 && filteredRows ? (
            <span className="font-body-md text-body-md text-on-surface-variant">
              {filteredRows.length} of {rows.length}
            </span>
          ) : null}
        </div>
      ) : null}

      <PropertyScopeBar
        crumbs={
          householdId
            ? [
                { label: 'Buildings', href: '/buildings' },
                {
                  label: 'Units',
                  href: scopedHref('/units', { building_id: buildingId }),
                },
                {
                  label: 'Households',
                  href: scopedHref('/households', { building_id: buildingId, unit_id: unitId }),
                },
                { label: householdLabel(householdId) },
              ]
            : []
        }
        clearHref="/residents"
        countLabel={
          householdId && rows
            ? `${scopedRows?.length ?? 0} of ${rows.length} residents`
            : undefined
        }
      />

      <CrudTable<ResidentRow>
        rows={scopedRows}
        error={error}
        loadingLabel="Loading residents…"
        emptyMessage={
          rows && rows.length > 0 && search.length > 0
            ? `No residents match "${search}". Try a shorter or different search.`
            : householdsActive.length === 0
              ? 'Add a household first, then add residents to it.'
              : 'No residents yet. Click Add resident to invite one.'
        }
        columns={[
          { key: 'name', header: 'Name', width: '20%' },
          {
            key: 'email',
            header: 'Email',
            render: (r) =>
              r.email ?? <span className="text-on-surface-variant">—</span>,
          },
          {
            key: 'household',
            header: 'Household',
            render: (r) => householdLabel(r.household_id),
          },
          {
            key: 'relationship',
            header: 'Relationship',
            width: '12%',
            render: (r) => (
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${REL_TONE[r.relationship]}`}
              >
                {r.relationship}
              </span>
            ),
          },
          {
            key: 'user_id',
            header: 'Portal',
            width: '10%',
            render: (r) =>
              r.user_id ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase bg-secondary-container text-on-secondary-container">
                  Linked
                </span>
              ) : (
                <span className="text-on-surface-variant">—</span>
              ),
          },
        ]}
        actions={(r) => (
          <RowActions
            actions={[
              { label: 'Edit', onClick: () => setModal({ kind: 'edit', row: r }) },
              {
                label: 'Remove',
                destructive: true,
                onClick: () => setModal({ kind: 'remove', row: r }),
              },
            ]}
          />
        )}
      />

      {modal ? (
        <ResidentModal
          state={modal}
          households={householdsActive}
          householdLabel={householdLabel}
          onCancel={() => setModal(null)}
          onDone={(msg, inviteToken) => {
            setModal(null);
            setToast({ msg, inviteToken });
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function InviteToast({
  toast,
  onDismiss,
}: {
  toast: { msg: string; inviteToken?: string };
  onDismiss: () => void;
}) {
  const { state } = useAuth();
  const isMaster =
    state.status === 'authed' && state.user.role === 'master_admin';
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!toast.inviteToken) {
    return <Toast message={toast.msg} onDismiss={onDismiss} />;
  }
  const link = `${window.location.origin}/accept-invite?token=${toast.inviteToken}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div
      role="status"
      className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-3 font-body-md text-body-md space-y-2"
    >
      <div className="flex items-center gap-2">
        <Icon name="check_circle" />
        <span className="flex-1">{toast.msg}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="underline font-medium"
        >
          Dismiss
        </button>
      </div>
      {isMaster ? (
        <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/40">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
              aria-label="Reveal invite link (admin only)"
            />
            <span>Show invite link (admin only)</span>
          </label>
          {show ? (
            <>
              <code className="flex-1 truncate text-on-surface bg-surface-container-lowest px-2 py-1 rounded">
                {link}
              </code>
              <button
                type="button"
                onClick={copy}
                className="underline font-medium"
                aria-label="Copy invite link"
              >
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ResidentModal({
  state,
  households,
  householdLabel,
  onCancel,
  onDone,
}: {
  state: ModalState;
  households: HouseholdLite[];
  householdLabel: (id: string) => string;
  onCancel: () => void;
  onDone: (toast: string, inviteToken?: string) => void;
}) {
  const { kind, row } = state;
  const { key: idemKey } = useIdempotencyKey();
  const [householdId, setHouseholdId] = useState(
    row?.household_id ?? households[0]?.id ?? '',
  );
  const [name, setName] = useState(row?.name ?? '');
  const [email, setEmail] = useState(row?.email ?? '');
  // Don't pester people with an error message until they've actually
  // touched the field once (or it was pre-filled from an existing row).
  const [emailTouched, setEmailTouched] = useState(!!row?.email);
  const [phone, setPhone] = useState(row?.phone ?? '');
  const [relationship, setRelationship] = useState<
    ResidentRow['relationship']
  >(row?.relationship ?? 'occupant');
  const [invite, setInvite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (kind === 'create') {
        const body = CreateResidentBody.parse({
          household_id: householdId,
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          relationship,
          invite,
        });
        const res = await apiClient
          .withIdempotencyKey(idemKey)
          .post<{ data: { resident: ResidentRow; invite_token: string | null } }>(
            '/api/v1/residents',
            body,
          );
        const tok = res.data.invite_token ?? undefined;
        onDone(
          invite
            ? `Resident "${body.name}" invited — invite sent to ${body.email}.`
            : `Resident "${body.name}" added.`,
          tok ?? undefined,
        );
        return;
      }
      if (kind === 'edit') {
        const body = UpdateResidentBody.parse({
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          relationship,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .patch(`/api/v1/residents/${row!.id}`, body);
        onDone(`Resident "${name.trim()}" updated.`);
        return;
      }
      if (kind === 'remove') {
        await apiClient.delete(`/api/v1/residents/${row!.id}`);
        onDone(`Resident "${row!.name}" removed.`);
        return;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed');
      setSubmitting(false);
    }
  }

  const title =
    kind === 'create'
      ? 'Add resident'
      : kind === 'edit'
        ? `Edit ${row?.name}`
        : `Remove ${row?.name}`;
  const tone: 'default' | 'destructive' = kind === 'remove' ? 'destructive' : 'default';
  const icon = kind === 'remove' ? 'person_remove' : kind === 'edit' ? 'edit' : 'person_add';
  const submitLabel =
    kind === 'create' ? (invite ? 'Send invite' : 'Add') : kind === 'edit' ? 'Save' : 'Remove';

  return (
    <CrudModal
      title={title}
      icon={icon}
      tone={tone}
      submitting={submitting}
      error={error}
      submitLabel={submitLabel}
      canSubmit={
        kind === 'remove' ||
        (name.trim().length > 0 &&
          (kind === 'edit' || householdId.length > 0) &&
          (!invite || email.trim().length > 0))
      }
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      {kind === 'remove' ? (
        <p className="font-body-md text-body-md text-on-surface">
          Remove <strong>{row?.name}</strong> from the property. If they have a
          portal account, it will be suspended and all sessions revoked.
        </p>
      ) : (
        <>
          {kind === 'create' && (
            <FormField id="resident-household" label="Household">
              <select
                id="resident-household"
                required
                value={householdId}
                onChange={(e) => setHouseholdId(e.target.value)}
                className={fieldCls}
              >
                {households.map((h) => (
                  <option key={h.id} value={h.id}>
                    {householdLabel(h.id)}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          <FormField id="resident-name" label="Name" required>
            <input
              id="resident-name"
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={fieldCls}
            />
          </FormField>
          <FormField
            id="resident-email"
            label="Email"
            required={invite}
            hint={
              invite
                ? "We'll send the sign-in link here."
                : 'Optional — only needed if you want them to sign in to the portal.'
            }
            error={
              emailTouched && email.length > 0 && !isLikelyEmail(email)
                ? "That doesn't look like a valid email — check for a typo."
                : null
            }
          >
            <input
              id="resident-email"
              type="email"
              maxLength={254}
              required={invite}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              className={fieldCls}
            />
          </FormField>
          <FormField id="resident-phone" label="Phone" hint="Optional.">
            <input
              id="resident-phone"
              maxLength={40}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={fieldCls}
            />
          </FormField>
          <FormField id="resident-relationship" label="Relationship">
            <select
              id="resident-relationship"
              value={relationship}
              onChange={(e) =>
                setRelationship(
                  ResidentRelationship.parse(e.target.value) as ResidentRow['relationship'],
                )
              }
              className={fieldCls}
            >
              <option value="leaseholder">Leaseholder</option>
              <option value="occupant">Occupant</option>
              <option value="minor">Minor</option>
              <option value="caregiver">Caregiver</option>
            </select>
          </FormField>
          {kind === 'create' && (
            <label className="inline-flex items-center gap-2 font-body-md text-body-md text-on-surface">
              <input
                type="checkbox"
                checked={invite}
                onChange={(e) => setInvite(e.target.checked)}
              />
              <span>Send portal invite email</span>
            </label>
          )}
        </>
      )}
    </CrudModal>
  );
}
